import { Component, inject, Input, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { LoaderService } from '@tcc-mono/shared/ui/services';
import { withLoading } from '@tcc-mono/shared/utils';
import { AnimalSelection, Hub, Stable, TransportGroupRvoReportResult } from '@tcc-mono/vandijk/shared/data-access/models';
import { AnimalSelectionRepository, HubsRepository } from '@tcc-mono/vandijk/shared/data-access/state';
import { HubService } from '@tcc-mono/vandijk/shared/data-access/vandijk-api';
import { StableSelectorComponent } from '@tcc-mono/vandijk/shared/ui/entity-selectors/stable-selector';
import { TransportGroupRvoResultDialogComponent } from '@tcc-mono/vandijk/web/office/feature/transport-groups/transport-group-rvo-result-dialog';
import { HubPickerComponent } from '@tcc-mono/vandijk/web/office/ui/hub-picker';
import dayjs from 'dayjs';
import { Observable, filter, from, map, switchMap, take, tap } from 'rxjs';

@Component({
  templateUrl: './animal-selection-transport-dialog.component.html',
  styleUrls: ['./animal-selection-transport-dialog.component.scss']
})
export class AnimalSelectionTransportDialogComponent implements OnInit {
  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _repo: AnimalSelectionRepository = inject(AnimalSelectionRepository);
  private readonly _loading: LoaderService = inject(LoaderService);
  private readonly _modalCtrl: ModalController = inject(ModalController);
  private readonly _hubRepo: HubsRepository = inject(HubsRepository);

  @Input() public selection: AnimalSelection;

  public form: FormGroup;
  public hubs$: Observable<Hub[]> = this._hubRepo.hubs$;

  public ngOnInit(): void {
    this.form = this._initForm();
    this._hubRepo.getHubs().pipe(take(1)).subscribe();
  }

  public editStable(): void {
    from(
      this._modalCtrl.create({
        component: StableSelectorComponent,
        componentProps: {
          params: {
            has_rvo: true
          }
        }
      })
    )
      .pipe(
        tap(modal => modal.present()),
        switchMap(modal => modal.onDidDismiss<Stable>()),
        map(({ data }) => data),
        filter((stable) => !!stable),
        tap((stable: Stable) => {
          this.form.get('stable_id').setValue(stable.id);
          this.form.get('stable_name').setValue(stable.name);
          this.form.get('stable_relation_name').setValue(stable.relation_name);
        })
      )
      .subscribe();
  }

  public get stableName(): string {
    return this.form?.get('stable_name')?.value
      ? `${this.form.get('stable_name').value} (${this.form.get('stable_relation_name').value})`
      : '-';
  }

  public save() {
    if (this.form.get('transportType').value == 'stable') {
      this.form.get('stable_id').setValidators([Validators.required]);
      this.form.get('hub_id').setValidators([]);
    } else {
      this.form.get('stable_id').setValidators([]);
      this.form.get('hub_id').setValidators([Validators.required]);
    }

    this.form.get('stable_id').updateValueAndValidity();
    this.form.get('hub_id').updateValueAndValidity();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const fv = this.form.value;

    const body = {
      date: fv.date,
      stable_id: fv.stable_id,
      hub_id: fv.hub_id
    };

    this._repo
      .transport(this.selection.id, body)
      .pipe(
        withLoading(this._loading),
        take(1),
        switchMap((results: any) => {
          return from(this._modalCtrl.create({
            component: TransportGroupRvoResultDialogComponent,
            componentProps: {
              rvoResults: results.data
            }
          }))
            .pipe(
              tap((modal) => modal.present())
            )
        }),
      )
      .subscribe({
        next: () => this._modalCtrl.dismiss()
      });
  }

  private _initForm(): FormGroup {
    return this._fb.group({
      date: [dayjs().format('YYYY-MM-DD'), [Validators.required]],
      stable_id: [null],
      stable_name: [null],
      stable_relation_name: [null],
      transportType: ['stable'],
      hub_id: [null]
    });
  }
}
